import { useCallback, useEffect, useMemo, useState } from 'react'
import { capabilitiesForProfile } from 'proctoring-sdk/config'
import type { UseProctoringReturn } from 'proctoring-sdk/types'
import { PERMISSION_DEFS } from 'Components/Proctoring/proctoringPermissions.constants'
import {
    replaceMediaTrack,
    statusFor,
    trackDeviceId,
} from 'Components/Proctoring/proctoringPermissions.helpers'
import {
    MediaTrackKind,
    PermissionKind,
    PermissionStatus,
} from 'Components/Proctoring/proctoring.types'
import type { PermissionItemState } from 'Components/Proctoring/proctoring.types'

export const useProctoringPermissionItems = (proctoring: UseProctoringReturn, profile?: string) => {
    const mediaCapabilities = useMemo(() => capabilitiesForProfile(profile), [profile])
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([])
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
    const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null)
    const [selectedAudioId, setSelectedAudioId] = useState<string | null>(null)

    const webcamStream = proctoring.state?.sdkState?.webcamStream ?? null
    const screenStream = proctoring.state?.sdkState?.screenStream ?? null

    const refreshDevices = useCallback(async () => {
        if (!navigator.mediaDevices?.enumerateDevices) return
        const devices = await navigator.mediaDevices.enumerateDevices()
        const cameras = devices.filter(d => d.kind === 'videoinput')
        const mics = devices.filter(d => d.kind === 'audioinput')
        setVideoDevices(cameras)
        setAudioDevices(mics)

        const fromSdkCameras = proctoring.devices?.cameras
        const fromSdkMics = proctoring.devices?.microphones
        if (fromSdkCameras?.length) setVideoDevices(fromSdkCameras)
        if (fromSdkMics?.length) setAudioDevices(fromSdkMics)
    }, [proctoring.devices])

    useEffect(() => {
        const webcamStatus = statusFor(proctoring, PermissionKind.Webcam)
        const micStatus = statusFor(proctoring, PermissionKind.Microphone)
        const screenStatus = statusFor(proctoring, PermissionKind.Screen)
        if (
            webcamStatus === PermissionStatus.Granted ||
            micStatus === PermissionStatus.Granted ||
            screenStatus === PermissionStatus.Granted
        ) {
            void refreshDevices()
        }
    }, [proctoring, refreshDevices, webcamStream, screenStream])

    useEffect(() => {
        const videoId = trackDeviceId(webcamStream, MediaTrackKind.Video)
        const audioId = trackDeviceId(webcamStream, MediaTrackKind.Audio)
        if (videoId) setSelectedVideoId(videoId)
        if (audioId) setSelectedAudioId(audioId)
    }, [webcamStream])

    const handleVideoDeviceChange = useCallback(
        async (deviceId: string) => {
            setSelectedVideoId(deviceId)
            try {
                await replaceMediaTrack(webcamStream, MediaTrackKind.Video, deviceId)
            } catch (e) {
                console.error('[proctoring] failed to switch camera', e)
            }
        },
        [webcamStream]
    )

    const handleAudioDeviceChange = useCallback(
        async (deviceId: string) => {
            setSelectedAudioId(deviceId)
            try {
                await replaceMediaTrack(webcamStream, MediaTrackKind.Audio, deviceId)
            } catch (e) {
                console.error('[proctoring] failed to switch microphone', e)
            }
        },
        [webcamStream]
    )

    const permissionItems = useMemo<PermissionItemState[]>(
        () =>
            PERMISSION_DEFS.filter(def => {
                if (def.kind === PermissionKind.Webcam) return !!mediaCapabilities.video
                if (def.kind === PermissionKind.Microphone) return !!mediaCapabilities.audio
                return !!mediaCapabilities.screen
            }).map(def => {
                if (def.kind === PermissionKind.Webcam) {
                    return {
                        ...def,
                        status: statusFor(proctoring, PermissionKind.Webcam),
                        availableDevices: videoDevices,
                        selectedDeviceId: selectedVideoId,
                        onDeviceChange: handleVideoDeviceChange,
                    }
                }
                if (def.kind === PermissionKind.Microphone) {
                    return {
                        ...def,
                        status: statusFor(proctoring, PermissionKind.Microphone),
                        availableDevices: audioDevices,
                        selectedDeviceId: selectedAudioId,
                        onDeviceChange: handleAudioDeviceChange,
                    }
                }
                return {
                    ...def,
                    status: statusFor(proctoring, PermissionKind.Screen),
                    availableDevices: [] as MediaDeviceInfo[],
                    selectedDeviceId: null,
                    onDeviceChange: undefined,
                }
            }),
        [
            mediaCapabilities,
            proctoring,
            videoDevices,
            audioDevices,
            selectedVideoId,
            selectedAudioId,
            handleVideoDeviceChange,
            handleAudioDeviceChange,
        ]
    )

    return { permissionItems, refreshDevices, webcamStream, screenStream }
}
